<script setup lang="ts">
import { hasCustomProperties } from '@@/bits/post_properties'
import { getPollFromPost } from '@@/bits/surface_polls'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import OzIndeterminateSpinner from '@@/library/v4/components/OzIndeterminateSpinner.vue'
import OzPlainButton, {
  OzPlainButtonColorScheme,
  OzPlainButtonSizePreset,
} from '@@/library/v4/components/OzPlainButton.vue'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfacePostPropertiesStore } from '@@/pinia/surface_post_properties'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useWindowSizeStore } from '@@/pinia/window_size'
import type { CustomPropertyTextRefs } from '@@/vuecomponents/SurfacePostProperties.vue'
import SurfacePostProperties from '@@/vuecomponents/SurfacePostProperties.vue'
import SurfaceSlideshowAttachment from '@@/vuecomponents/SurfaceSlideshowAttachment.vue'
import { processPostBody } from '@@/vuecomposables/process_post_or_comment_body'
import { useSlideshowAttachments } from '@@/vuecomposables/slideshow_attachments'
import { useSlideshowResizePostTextV2 } from '@@/vuecomposables/slideshow_resize_post_text_v2'
import { useSlideshowStyles } from '@@/vuecomposables/slideshow_styles'
import { storeToRefs } from 'pinia'
import type { Ref } from 'vue'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'

const props = defineProps<{
  postCid: string
  isCurrentlyViewed: boolean
}>()

const { colorScheme } = storeToRefs(useSurfaceStore())
const { accentColorStyle, primaryColorStyle, textColorClass, hasDarkBackgroundLuminance } = useSlideshowStyles()
const {
  isSmallerThanDesktop,
  isSmallerThanDesktop2XL,
  isSmallerThanDesktop3XL,
  isSmallerThanTabletLandscape,
  isSmallerThanTabletPortrait,
  isBiggerThanTabletPortrait,
} = storeToRefs(useWindowSizeStore())

const { areAttachmentsLoadedForCurrentPost } = useSlideshowAttachments()
const postContainerRef = ref<HTMLDivElement>()
const postTextContainerRef = ref<HTMLDivElement>()
const postSubjectRef = ref<HTMLDivElement>()
const postBodyRef = ref<HTMLDivElement>()
const postBodyWithCustomPropertiesContainer = ref<HTMLDivElement>()
const postAttachmentRef = ref<HTMLDivElement>()

const customPropertyLabelRefs: Ref<HTMLElement[]> = ref([])
const customPropertyValueRefs: Ref<HTMLElement[]> = ref([])
function setCustomPropertyTextRefs(e: CustomPropertyTextRefs) {
  customPropertyLabelRefs.value = e.customPropertyLabelRefs.value
  customPropertyValueRefs.value = e.customPropertyValueRefs.value
}

const { postEntitiesByCid } = storeToRefs(useSurfacePostsStore())
const surfacePostPropertiesStore = useSurfacePostPropertiesStore()
const { wallCustomPostProperties } = storeToRefs(surfacePostPropertiesStore)

const currentPost = computed(() => postEntitiesByCid.value[props.postCid])
const hasPostText = computed(
  () => currentPost.value.subject || currentPost.value.body || hasCustomProperties(currentPost.value),
)
const isCurrentlyViewedRef = computed(() => props.isCurrentlyViewed)
const postAttachmentWidth = ref<number | undefined>(postAttachmentRef.value?.clientWidth)
const poll = computed(() => getPollFromPost(currentPost.value))
const hasPostAttachment = computed(() => currentPost.value.attachment || poll.value)
const pollAttachmentHeight = computed(() => {
  if (postContainerRef.value && isSmallerThanTabletLandscape.value) {
    return postContainerRef.value.clientHeight / 2
  }

  return 0
})
const xPostProperties = computed(() => surfacePostPropertiesStore.shouldShowPostProperties(currentPost.value))

const setPostAttachmentWidth = () => {
  postAttachmentWidth.value = postAttachmentRef.value?.clientWidth
}

const scrollButtonIconSize = computed(() => {
  if (isSmallerThanDesktop.value) {
    return 20
  }
  if (isSmallerThanDesktop2XL.value) {
    return 24
  }
  if (isSmallerThanDesktop3XL.value) {
    return 28
  }
  return 32
})

const scrollButtonIconSizePreset = computed(() => {
  if (isSmallerThanTabletLandscape.value) {
    return OzPlainButtonSizePreset.H28px
  }
  if (isSmallerThanDesktop.value) {
    return OzPlainButtonSizePreset.H32px
  }
  return OzPlainButtonSizePreset.H44px
})

const { shouldShowScrollButton, scrollDownPostText } = useSlideshowResizePostTextV2({
  postContainerRef,
  postTextContainerRef,
  postBodyWithCustomPropertiesContainer,
  postBodyRef,
  postSubjectRef,
  postAttachmentRef,
  currentPost,
  isAttachmentLoaded: areAttachmentsLoadedForCurrentPost,
  isCurrentlyViewed: isCurrentlyViewedRef,
  customPropertyLabelRefs,
  customPropertyValueRefs,
})

const customLinkButtonPropertySize = computed(() => {
  if (isSmallerThanTabletPortrait.value) return 32
  if (isSmallerThanDesktop.value) return 40
  return 56
})

watch(areAttachmentsLoadedForCurrentPost, () => {
  if (areAttachmentsLoadedForCurrentPost.value) {
    // Hack: Fixes sizing for problematic attachments (e.g. Youtube videos) that sometimes don't size correctly initially.
    // This `resize` event will trigger the handlers in `SurfaceSlideshowAttachment` and its child components to resize the attachment correctly.
    window.dispatchEvent(new Event('resize'))
  }
})

// =========================
// Format the post body to have links open in a new tab and mathify the post body
// only if the post body has loaded and is currently being viewed.
// =========================
watch(
  [
    () => props.isCurrentlyViewed,
    postBodyRef,
    // Watch body because it can be updated by realtime
    () => currentPost.value.body,
  ],
  async ([isCurrentlyViewed, postBodyRef, currentPostBody]) => {
    // post body processing only works when the post is displayed; mathify does not work on posts in the background.
    if (!isCurrentlyViewed || postBodyRef === undefined || currentPostBody === undefined) return

    await processPostBody(currentPostBody, postBodyRef)

    // we want to resize the post text since mathifying may cause a text height increase and overflow
    window.dispatchEvent(new Event('resize'))
  },
)

onMounted(() => {
  window.addEventListener('resize', setPostAttachmentWidth)
})

onUnmounted(() => {
  window.removeEventListener('resize', setPostAttachmentWidth)
})
</script>

<template>
  <div
    :class="[
      'h-full w-full',
      'flex flex-col',
      'relative',
      'box-border',
      'px-6 desktop-2xl:px-10 desktop-3xl:px-20',
      'pt-12 tablet-landscape:pt-10 desktop:pt-12 desktop-3xl:pt-20',
    ]"
    :style="{ backgroundColor: primaryColorStyle }"
  >
    <div
      ref="postContainerRef"
      :class="[
        'h-full w-full',
        'relative',
        'flex justify-center items-center',
        'box-border',
        hasPostAttachment &&
          hasPostText && [
            'flex-col-reverse tablet-landscape:grid tablet-landscape:grid-cols-2',
            'tablet-landscape:px-12 desktop:px-16 desktop-2xl:px-48 desktop-3xl:px-60',
            // Let postTextContainer control the gap in single column layout
            'tablet-landscape:gap-x-12 desktop:gap-x-16 desktop-2xl:gap-x-48 desktop-3xl:gap-x-60',
          ],
      ]"
    >
      <!-- Ideally, this would be placed inside postTextContainer but the position cannot escape a scrollable container -->
      <!-- This also means we need to position the chevron relative to the overall container -->
      <!-- even though the underlying design positions the chevron relative to the text container -->
      <Transition name="fade">
        <OzPlainButton
          v-if="isBiggerThanTabletPortrait && shouldShowScrollButton"
          :data-testid="'slideshowPagePostScrollDownButton'"
          :class="[
            'absolute',
            'tablet-portrait:w-7 tablet-landscape:w-8 desktop:w-11',
            'mx-auto',
            'inset-x-0',
            hasPostAttachment && 'tablet-landscape:end-1/2',
            // Bottom values = postTextContainer bottom padding - Icon size - Margin between icon and bottom of post text
            'tablet-landscape:bottom-13 desktop:bottom-19 desktop-2xl:bottom-31 desktop-3xl:bottom-43',
            hasPostAttachment ? 'tablet-portrait:bottom-2' : 'tablet-portrait:bottom-14',
          ]"
          :title="__('Scroll down on post text')"
          :aria-label="__('Scroll down on post text')"
          icon-name="disclosure"
          :dark-mode="hasDarkBackgroundLuminance"
          :color-scheme="OzPlainButtonColorScheme.SecondaryIcon"
          :size-preset="scrollButtonIconSizePreset"
          @click="scrollDownPostText"
        >
          <OzIcon :size="scrollButtonIconSize" name="disclosure" />
        </OzPlainButton>
      </Transition>
      <div
        v-if="hasPostText"
        ref="postTextContainerRef"
        :data-testid="'slideshowPagePostTextContainer'"
        :class="[
          'w-fit max-w-full',
          'no-scrollbar',
          'box-border',
          'justify-self-center',
          'flex flex-col',
          'gap-3 desktop:gap-4 desktop-2xl:gap-9',
          hasPostAttachment
            ? [
                // Let postContainer control the outer padding in two column layout
                'px-12 tablet-portrait:px-24 tablet-landscape:px-0',
                'py-6 tablet-portrait:py-12 tablet-landscape:py-24 desktop:py-32 desktop-2xl:py-48 desktop-3xl:py-60',
                'tablet-landscape:w-full',
              ]
            : [
                'pt-12 pb-6 px-0',
                'tablet-portrait:p-24',
                'tablet-landscape:px-30 tablet-landscape:py-24',
                'desktop:px-40 desktop:py-32 ',
                'desktop-2xl:px-120 desktop-2xl:py-48 ',
                'desktop-3xl:px-150 desktop-3xl:py-60 ',
              ],
        ]"
      >
        <h1
          v-if="currentPost.subject"
          ref="postSubjectRef"
          :class="[
            'font-semibold',
            'text-start',
            'text-slideshow-heading-sm tablet-portrait:text-slideshow-heading-md desktop:text-slideshow-heading-lg desktop-2xl:text-slideshow-heading-xl desktop-3xl:text-slideshow-heading-2xl',
            'break-word-anywhere',
          ]"
          :style="{ color: accentColorStyle }"
          :data-testid="'slideshowPagePostSubject'"
        >
          {{ currentPost.subject }}
        </h1>
        <div
          ref="postBodyWithCustomPropertiesContainer"
          :class="['flex flex-col', 'gap-3 desktop:gap-4 desktop-2xl:gap-9', 'no-scrollbar']"
          data-testid="slideshowPagePostBodyWithCustomPropertiesContainer"
        >
          <!-- eslint-disable vue/no-v-html -->
          <p
            v-show="currentPost.body /* use v-show so body is always in the DOM as expected by post processing */"
            ref="postBodyRef"
            :class="[
              'post-body-content',
              hasDarkBackgroundLuminance ? 'post-body-content-dark' : 'post-body-content-light',
              'box-border',
              'text-start',
              'text-slideshow-body-sm tablet-portrait:text-slideshow-body-md desktop:text-slideshow-body-lg desktop-2xl:text-slideshow-body-xl desktop-3xl:text-slideshow-body-2xl',
              textColorClass,
              'break-word-anywhere',
            ]"
            :data-testid="'slideshowPagePostBody'"
            v-html="currentPost.body"
          />
          <!-- eslint-enable -->
          <SurfacePostProperties
            v-if="xPostProperties"
            :source="'slideshow'"
            :dark-mode="hasDarkBackgroundLuminance"
            :location-name="currentPost.location_name"
            :wall-custom-post-properties="wallCustomPostProperties"
            :custom-property-values="currentPost.custom_properties"
            :link-button-size="customLinkButtonPropertySize"
            :post-color="currentPost.color"
            :color-scheme="colorScheme"
            property-label-class="text-slideshow-body-xs tablet-portrait:text-slideshow-body-sm desktop:text-slideshow-body-md desktop-2xl:text-slideshow-body-lg desktop-3xl:text-slideshow-body-xl"
            property-value-class="text-slideshow-body-sm tablet-portrait:text-slideshow-body-md desktop:text-slideshow-body-lg desktop-2xl:text-slideshow-body-xl desktop-3xl:text-slideshow-body-2xl"
            @update:text-element-refs="setCustomPropertyTextRefs"
          />
        </div>
      </div>
      <div
        v-if="hasPostAttachment"
        ref="postAttachmentRef"
        :class="[
          'w-full',
          'flex justify-center items-center',
          hasPostText ? 'min-h-1/2 h-auto tablet-landscape:min-h-0 tablet-landscape:h-full' : 'h-full',
        ]"
      >
        <SurfaceSlideshowAttachment
          :is-full-page="!hasPostText"
          :post="currentPost"
          :dark-mode="hasDarkBackgroundLuminance"
          :is-currently-viewed="isCurrentlyViewed"
          :container-width="postAttachmentWidth || 0"
          :container-height="pollAttachmentHeight"
          :slideshow-background-color="primaryColorStyle"
        />
      </div>
    </div>
    <!-- Spinner intentionally rendered on top of post content, so attachment can load in background -->
    <div
      v-show="currentPost.attachment && !areAttachmentsLoadedForCurrentPost"
      :class="['h-full w-full', 'flex justify-center items-center', 'absolute inset-0', 'z-10']"
      :style="{ backgroundColor: primaryColorStyle }"
    >
      <OzIndeterminateSpinner :dark-mode="hasDarkBackgroundLuminance" :size="40" />
    </div>
  </div>
</template>

<style lang="postcss">
/* styles from SurfacePostBody.vue, excluding variants for different post colors & system-dark mode */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease-out;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.post-body-content {
  a {
    @apply underline;
    @apply text-current;
    text-underline-position: from-font;
    text-decoration-thickness: from-font;
  }

  blockquote {
    @apply border-solid;
    @apply border-s-4;
    @apply italic;
    @apply px-4;
  }

  mark {
    @apply text-current;
  }

  pre {
    @apply font-mono;
    @apply rounded-lg;
    @apply whitespace-pre-wrap;
    @apply py-1.5;
    @apply px-2;
    @apply -mx-1;
    @apply my-1.5;
    @apply text-start;
    tab-size: 2;
  }

  var {
    @apply font-mono;
    @apply block;
    @apply text-center;
    direction: ltr;
  }

  ol {
    @apply list-decimal;
  }

  ul {
    @apply list-disc;
  }

  ol,
  ul {
    @apply list-outside;
    padding-inline-start: 2em;
    li {
      @apply py-1;
      @apply px-0;
    }
  }

  &.post-body-content-light {
    blockquote {
      @apply border-dark-text-300;
    }

    mark {
      @apply bg-tangerine-300;
    }

    pre {
      @apply bg-light-ui-300;
    }
  }

  &.post-body-content-dark {
    blockquote {
      @apply border-light-text-300;
    }

    mark {
      @apply bg-canary-800;
    }

    pre {
      @apply bg-dark-ui-200;
    }
  }
}
</style>
